import { Player } from '@lottiefiles/react-lottie-player';
import { graphql, useStaticQuery } from 'gatsby';
import { DateTime } from 'luxon';
import { uniq } from 'ramda';
import React, { FC, useState } from 'react';
import { useDeepCompareEffect } from 'react-use';
import styled from 'styled-components';

import BasicText from '@ui/atoms/BasicText';
import Button from '@ui/atoms/Button';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import LoadingWrapper from '@ui/atoms/LoadingWrapper';
import Skeleton from '@ui/atoms/Skeleton';

import CenteredGridItem from '@ui/molecules/CenteredGridItem';

import fullWidth from '@ui/styled/FullWidth';
import spaced from '@ui/styled/Spaced';

import useMemberBills, { IMemberBill } from '@hooks/useMemberBills';

import { keysToCamel } from '@utils/butter';
import { doesNotExist, exists } from '@utils/general';

import { IPageProps } from '@folx-types/flow-steps';

import PageLoading from '@assets/lottie/PageLoading.json';

import BillsListStep, { IBillsListStepContent } from './BillsListStep.component';

interface IBillsListStepPageProps extends IPageProps {
	path: string;
}

const FullWidthColumn = fullWidth<IFlexColumn>(FlexColumn);
const SpacedColumn = spaced<IFlexColumn>(FullWidthColumn);

const StyledPlayer = styled(fullWidth(Player))`
	height: 12rem;
`;

const BillsListStepPage: FC<IBillsListStepPageProps> = ({ path }) => {
	const { pageContent } = useStaticQuery(graphql`
		{
			pageContent: butterPage(slug: { eq: "mssp-outstanding-medication-bills-step" }) {
				title
				disclaimer
				breadcrumb_label
				load_more_label
				empty_medication_bills_text
			}
		}
	`);

	const refinedContent: IBillsListStepContent = keysToCamel(pageContent) as IBillsListStepContent;

	const monthInterval = 2;
	const initialDate = DateTime.now().minus({ months: monthInterval }).endOf('day');
	const [startDate, setStartDate] = useState<DateTime>(initialDate);
	const [firstBillDate, setFirstBillDate] = useState<DateTime | undefined>(initialDate);
	const [bills, setBills] = useState<IMemberBill[]>([]);

	const {
		data: billsData,
		loading: billsLoading,
		error: billsError,
		refetch: refetchBills,
	} = useMemberBills(
		startDate?.toISO() ?? '',
		startDate?.plus({ months: monthInterval })?.toISO() ?? ''
	);

	useDeepCompareEffect(() => {
		if (billsLoading) {
			return;
		}

		if (exists(billsData?.bills)) {
			const billsList = uniq(bills.concat(billsData?.bills));
			setBills(billsList);
		}

		if (exists(billsData?.firstBillDate)) {
			const firstBill = DateTime.fromISO(billsData?.firstBillDate ?? '');
			setFirstBillDate(firstBill);
		}

		if (
			doesNotExist(billsData?.bills) &&
			doesNotExist(bills) &&
			exists(billsData?.lastBillDate) &&
			startDate > DateTime.fromISO(billsData?.lastBillDate)
		) {
			const lastBill = DateTime.fromISO(billsData?.lastBillDate ?? '');
			setStartDate(lastBill.minus({ months: monthInterval }).endOf('day'));
			return;
		}
	}, [billsData, billsLoading]);

	useDeepCompareEffect(() => {
		if (doesNotExist(startDate) || startDate === initialDate) {
			return;
		}

		refetchBills(
			startDate?.toISO() ?? '',
			startDate?.plus({ months: monthInterval })?.toISO() ?? ''
		);
	}, [startDate]);

	useDeepCompareEffect(() => {
		if (!billsError) return;
		console.log(billsError);
	}, [billsError]);

	const loading = billsLoading && doesNotExist(bills);
	const showLoadMoreBills =
		exists(firstBillDate) && exists(startDate) && startDate >= firstBillDate;

	const skeleton = (
		<FullWidthColumn gap="md">
			<Skeleton height="sm" fullWidth />
			<Skeleton height="lg" fullWidth />
			<Skeleton height="xxxxl" fullWidth />
			<Skeleton height="xxl" fullWidth />
		</FullWidthColumn>
	);

	return (
		<LoadingWrapper loading={loading} skeleton={skeleton}>
			<CenteredGridItem>
				<BillsListStep pageContent={refinedContent} path={path} bills={bills} />
				<SpacedColumn gap="sm" marginTop="lg">
					{billsLoading && <StyledPlayer autoplay loop src={PageLoading} />}
					{showLoadMoreBills && (
						<Button
							label={refinedContent.loadMoreLabel}
							variant="secondary"
							type="button"
							onClick={() => setStartDate(startDate?.minus({ months: monthInterval }).endOf('day'))}
						/>
					)}
					<BasicText>{refinedContent.disclaimer}</BasicText>
				</SpacedColumn>
			</CenteredGridItem>
		</LoadingWrapper>
	);
};

export default BillsListStepPage;
